// src/components/Login.js
import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../services/api";
import {
  ShieldCheck,
  User,
  Lock,
  Mail,
  Eye,
  EyeOff,
  LogIn,
  UserPlus,
  CheckCircle2,
  AlertCircle,
  FileSignature,
  Fingerprint,
  GlobeLock,
  RefreshCw,
} from "lucide-react";

export default function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState(["", "", "", "", "", ""]);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    fullName: "",
    website: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const { clearError } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setFormError(null);
    clearError();
  };

  const handleCodeChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    if (value && index < 5) {
      const nextInput = document.getElementById(`code-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleCodePaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newCode = [...verificationCode];
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newCode[i] = pastedData[i];
    }
    setVerificationCode(newCode);

    const nextIndex = Math.min(pastedData.length, 5);
    const nextInput = document.getElementById(`code-input-${nextIndex}`);
    if (nextInput) nextInput.focus();
  };

  const handleCodeKeyDown = (e, index) => {
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const startResendTimer = () => {
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 👇 función reutilizable: envía un código nuevo y solo arranca el timer si tuvo éxito
  const requestNewCode = async (email) => {
    setResendLoading(true);
    try {
      const response = await apiService.publicRequest("/auth/resend-verification", {
        method: "POST",
        body: { email },
      });

      if (response.success) {
        startResendTimer();
      }
      return response;
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setFormError(null);

    try {
      if (isLogin) {
        try {
          const response = await apiService.publicRequest("/auth/login", {
            method: "POST",
            body: {
              username: formData.username,
              password: formData.password,
            },
          });

          apiService.setToken(response.token);
          apiService.setUser(response.user);
          setFormData({ username: "", email: "", password: "", fullName: "" });
          window.location.reload();
        } catch (err) {
          if (err.data?.requiresVerification) {
            const email = err.data.email || formData.username;
            setVerificationEmail(email);
            setIsVerifying(true);

            // 👇 el código anterior pudo haber expirado — se envía uno nuevo automáticamente
            requestNewCode(email);
          } else {
            setFormError(err.message || "Credenciales inválidas");
          }
        }
      } else {
        try {
          const response = await apiService.publicRequest("/auth/register", {
            method: "POST",
            body: formData,
          });

          if (response.success && response.requiresVerification) {
            setVerificationEmail(formData.email);
            setIsVerifying(true);
            startResendTimer(); // aquí sí corresponde: el registro ya envió un código fresco
            setFormError(null);
          } else if (response.success) {
            setIsLogin(true);
            setFormData({ username: "", email: "", password: "", fullName: "" });
            setFormError(null);
          } else {
            setFormError(response.error || "Error en el registro");
          }
        } catch (err) {
          setFormError(err.message || "Error al conectar con el servidor");
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const code = verificationCode.join("");

    if (code.length !== 6) {
      alert("Por favor ingresa los 6 dígitos del código");
      return;
    }

    try {
      const response = await apiService.publicRequest("/auth/verify-email", {
        method: "POST",
        body: {
          email: verificationEmail,
          code: code,
        },
      });

      if (response.success) {
        apiService.setToken(response.token);
        apiService.setUser(response.user);

        setIsVerifying(false);
        setIsLogin(true);
        setVerificationCode(["", "", "", "", "", ""]);
        setVerificationEmail("");
        setFormData({ username: "", email: "", password: "", fullName: "" });

        window.location.reload();
      }
    } catch (err) {
      alert(err.message || "Código inválido o expirado");
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return;

    const response = await requestNewCode(verificationEmail);

    if (response.success) {
      alert("Nuevo código enviado correctamente. Revisa tu correo.");
    } else {
      alert(response.error || "Error al reenviar el código");
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setIsVerifying(false);
    setFormError(null);
    clearError();
    setFormData({ username: "", email: "", password: "", fullName: "" });
    setVerificationCode(["", "", "", "", "", ""]);
    setVerificationEmail("");
  };

  // ================= VISTA DE VERIFICACIÓN =================
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4 shadow-lg shadow-blue-600/20">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
              Verifica tu correo
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              Hemos enviado un código de 6 dígitos a:
            </p>
            <p className="text-sm font-semibold text-slate-900 break-all">
              {verificationEmail}
            </p>
            {resendLoading && (
              <p className="text-xs text-blue-600 text-center mt-2">Enviando código...</p>
            )}
          </div>

          <form onSubmit={handleVerifyCode} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3 text-center">
                Ingresa el código de verificación
              </label>
              <div className="flex gap-2 justify-center">
                {verificationCode.map((digit, index) => (
                  <input
                    key={index}
                    id={`code-input-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onPaste={(e) => handleCodePaste(e, index)}
                    onKeyDown={(e) => handleCodeKeyDown(e, index)}
                    className="w-12 h-14 text-center text-2xl font-bold border-2 border-slate-200 rounded-xl focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 focus:outline-none transition-all bg-white"
                  />
                ))}
              </div>
              <p className="text-xs text-slate-500 text-center mt-3">
                El código expira en 15 minutos
              </p>
            </div>

            <button
              type="submit"
              disabled={verificationCode.some((d) => d === "")}
              className="w-full bg-slate-900 text-white py-3 rounded-lg hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed transition-all font-medium flex items-center justify-center gap-2"
            >
              <CheckCircle2 className="w-5 h-5" />
              Verificar y Continuar
            </button>

            <div className="text-center">
              {resendTimer > 0 ? (
                <p className="text-sm text-slate-500">
                  Reenviar código en{" "}
                  <span className="font-semibold text-slate-700">{resendTimer}s</span>
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendLoading}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2 mx-auto disabled:text-slate-400"
                >
                  {resendLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      ¿No recibiste el código? Reenviar
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={toggleMode}
                className="w-full text-center text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                ← Volver al inicio
              </button>
            </div>
          </form>

          <div className="mt-8 flex items-center justify-center gap-2 text-xs text-slate-400">
            <ShieldCheck className="w-3 h-3" />
            <span>Conexión segura y encriptada</span>
          </div>
        </div>
      </div>
    );
  }

  // ================= VISTA NORMAL DE LOGIN/REGISTRO =================
  return (
    <div className="min-h-screen bg-white flex overflow-hidden font-sans">
      <div className="hidden lg:flex lg:w-1/2 bg-slate-950 relative flex-col justify-between p-12 xl:p-16">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        ></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-indigo-600/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-600/20">
              <FileSignature className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight">Inkless</h1>
              <p className="text-xs text-slate-400 font-medium tracking-wider uppercase">
                Enterprise Security
              </p>
            </div>
          </div>

          <div className="max-w-md space-y-8">
            <div>
              <h2 className="text-4xl xl:text-5xl font-semibold text-white tracking-tight leading-[1.15]">
                Firma digital con <br />
                <span className="text-blue-500">validez legal absoluta.</span>
              </h2>
              <p className="mt-6 text-lg text-slate-400 leading-relaxed">
                La plataforma corporativa diseñada para la gestión, firma y
                custodia de documentos PDF con los más altos estándares de
                seguridad global.
              </p>
            </div>

            <div className="space-y-4 pt-4">
              {[
                { icon: ShieldCheck, text: "Encriptación AES-256 de extremo a extremo" },
                { icon: Fingerprint, text: "Autenticación biométrica y doble factor (2FA)" },
                { icon: GlobeLock, text: "Cumplimiento normativo eIDAS y GDPR" },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 group">
                  <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-blue-600/20 group-hover:border-blue-500/30 transition-all duration-300">
                    <item.icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-sm font-medium text-slate-300">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-2 text-xs text-slate-500 border-t border-white/10 pt-8">
          <Lock className="w-3 h-3" />
          <span>Conexión segura y verificada • © 2026 Inkless Inc.</span>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-white relative">
        <div className="w-full max-w-[440px]">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <FileSignature className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">Inkless</h1>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 tracking-tight">
              {isLogin ? "Acceso a la plataforma" : "Crear cuenta corporativa"}
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              {isLogin
                ? "Ingrese sus credenciales para acceder a sus documentos."
                : "Complete los datos para registrar su organización."}
            </p>
          </div>

          {formError && (
            <div className="flex items-start gap-3 p-4 rounded-lg border mb-6 bg-red-50 border-red-200 text-red-800">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <p className="text-sm font-medium leading-snug">{formError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">

               {/*  CAMPO HONEYPOT: */}
            <input
              type="text"
              name="website"
              tabIndex="-1"
              autoComplete="off"
              style={{ display: 'none' }}
              value={formData.website}
              onChange={handleChange}
            />

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-700">Nombre de usuario</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <User className="w-4 h-4 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                </div>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="ej. jperez"
                  className="block w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 transition-all duration-200"
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            {!isLogin && (
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700">Correo electrónico corporativo</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Mail className="w-4 h-4 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="nombre@empresa.com"
                    className="block w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 transition-all duration-200"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>
            )}

            {!isLogin && (
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700">Nombre completo</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <User className="w-4 h-4 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                  </div>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Juan Pérez García"
                    className="block w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 transition-all duration-200"
                    required
                  />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-slate-700">Contraseña</label>
                {isLogin && (
                  <a href="#" className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors">
                    ¿Olvidó su contraseña?
                  </a>
                )}
              </div>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Lock className="w-4 h-4 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={isLogin ? "••••••••" : "Mínimo 8 caracteres"}
                  className="block w-full pl-10 pr-11 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 transition-all duration-200"
                  required
                  minLength={isLogin ? 1 : 8}
                  autoComplete={isLogin ? "current-password" : "new-password"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-8 bg-slate-900 text-white py-2.5 px-4 rounded-lg hover:bg-slate-800 active:bg-slate-950 disabled:bg-slate-300 disabled:cursor-not-allowed transition-all duration-200 font-medium text-sm shadow-lg shadow-slate-900/10 hover:shadow-xl hover:shadow-slate-900/20 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                  <span>{isLogin ? "Verificando credenciales..." : "Procesando registro..."}</span>
                </>
              ) : (
                <>
                  {isLogin ? <LogIn className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                  <span>{isLogin ? "Iniciar sesión segura" : "Crear cuenta"}</span>
                </>
              )}
            </button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-slate-500 font-medium">
                {isLogin ? "¿Primera vez en la plataforma?" : "¿Ya tiene una cuenta registrada?"}
              </span>
            </div>
          </div>

          <button
            onClick={toggleMode}
            className="w-full text-center text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 py-2 rounded-lg transition-all duration-200"
          >
            {isLogin ? "Solicitar acceso a la plataforma" : "Volver al inicio de sesión"}
          </button>

          <div className="mt-10 pt-6 border-t border-slate-100">
            <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
              <ShieldCheck className="w-3 h-3" />
              <span>Protegido por encriptación de extremo a extremo</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}